import React from 'react';
import PropTypes from 'prop-types';
import { Icon, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { middleEllipsis } from '../../../../utilities/StringUtilities';

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    text: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    helpIcon: {
        fontSize: '16px',
        color: theme.palette.text.secondary,
        cursor: 'help',
    },
}));

/**
 * BusinessServicesCell component displays business services with tooltip and help icon.
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.businessServices - Array of business service objects with 'name' property
 * @returns {JSX.Element} Formatted business services cell
 *
 * @example
 * // Single service
 * <BusinessServicesCell businessServices={[{ name: 'Service A' }]} />
 *
 * @example
 * // Multiple services (shows help icon)
 * <BusinessServicesCell businessServices={[
 *   { name: 'Service A' },
 *   { name: 'Service B' }
 * ]} />
 */
export function BusinessServicesCell({ businessServices }) {
    const classes = useStyles();

    if (!businessServices || businessServices.length === 0) {
        return <span>--</span>;
    }

    const bsNames = businessServices.map((bs) => bs.name || 'N/A').filter(Boolean);
    const displayText = middleEllipsis(bsNames[0], 32);
    const allNames = bsNames.join(', ');
    const showHelpIcon = bsNames.length > 1;

    return (
        <div className={classes.container}>
            <Tooltip title={allNames} placement="top">
                <span className={classes.text}>{displayText}</span>
            </Tooltip>
            {showHelpIcon && (
                <Tooltip title={allNames} placement="top">
                    <Icon className={classes.helpIcon} aria-label="View all business services">
                        help_outline
                    </Icon>
                </Tooltip>
            )}
        </div>
    );
}

BusinessServicesCell.propTypes = {
    businessServices: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
        })
    ),
};

BusinessServicesCell.defaultProps = {
    businessServices: [],
};
